import React, { useCallback, useEffect, useRef, useState } from 'react';
import ProcessMapsList from './List/ProcessMapsList';
import { getService } from '@Client/runner.hooks/useService';
import { Category } from '@Client/runner.directives/common/Category/Category';
import {
  IFlowModelDeletedMessage,
  IFlowModelPublishedMessage,
  IFlowModelUnpublishedMessage
} from '@Shared.Angular/@types/pubSub';
import { Guid } from '@Shared.Angular/@types/guid';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { IExpandedCategoryInfo } from '@Client/runner.nav.categories/components/CategoryNavigation';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import { FlowModelPublishType } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelPublishType';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

type Props = {
  backFromState: string;
};

const ProcessMaps = (props: Props) => {
  const { backFromState } = props;

  const allCategoriesId = 'all-categories';
  const recentlyViewedId = 'recently-viewed';

  const appConfig = getService('APP_CONFIG');
  const flowListManager = getService('flowListManager');
  const pubSubService = getService('pubsubService');
  const appInsightsService = getService('appInsightsService');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const permissionsService = getService('permissionsService');
  const flowinglyConstants = getService('flowinglyConstants');
  const $state = getService('$state');
  const brandingService = getService('brandingService');
  const isReturningFromProcessView =
    backFromState === STATE_MAPPINGS.processmapviewv2;
  const [expandedCategory, setExpandedCategory] = React.useState(() =>
    isReturningFromProcessView
      ? flowListManager.getExpandedCategoriesList()
      : null
  );
  const [selectedCategoryId, setSelectedCategoryId] = React.useState(() =>
    isReturningFromProcessView
      ? flowListManager.getProcessMapCategoryId() || allCategoriesId
      : allCategoriesId
  );
  const [categorisedProcessMaps, setCategorisedProcessMaps] = useState<
    ITreeCategory<IPublishedFlowModel>[]
  >([]);
  const [processMaps, setProcessMaps] = useState([]);
  const [filteredProcessMaps, setFilteredProcessMaps] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);

  const returningFromProcessView = useRef<boolean>(isReturningFromProcessView);

  const removeFlowModel = useCallback(
    (flowModelId: Guid) => {
      const removed = runnerCategoryNavigationService.removeItem(
        categorisedProcessMaps,
        flowModelId
      );
      if (removed) {
        setCategorisedProcessMaps([...categorisedProcessMaps]);
      }
    },
    [categorisedProcessMaps]
  );

  const addFlowModel = useCallback(
    (publishedFlowModel: IPublishedFlowModel) => {
      setCategorisedProcessMaps((prevCategories) => {
        runnerCategoryNavigationService.addItem(
          prevCategories,
          allCategoriesId,
          publishedFlowModel
        );
        return [...prevCategories];
      });
    },
    [allCategoriesId]
  );

  const removeDeletedActor = useCallback(
    (jsonParams: string) => {
      const params = JSON.parse(jsonParams);
      let updatedFlowModels = false;
      const updatedCategorisedProcessMaps = categorisedProcessMaps.map(
        (category) => {
          const updatedFlowModelsInCategory = category.flowModels.map(
            (flowModel) => {
              if (flowModel.processOwnerName === params.actorName) {
                updatedFlowModels = true;
                return { ...flowModel, processOwnerName: null };
              }
              return flowModel;
            }
          );
          return { ...category, flowModels: updatedFlowModelsInCategory };
        }
      );

      setCategorisedProcessMaps(updatedCategorisedProcessMaps);

      if (updatedFlowModels) {
        selectCategory(selectedCategoryId);
      }
    },
    [categorisedProcessMaps, selectedCategoryId]
  );

  const updateFlowModelName = useCallback(
    (jsonParams: string) => {
      const params = JSON.parse(jsonParams);
      const updatedMap = updateFlowModelNameInCategorisedData(
        categorisedProcessMaps,
        params.id,
        params.name
      );
      if (updatedMap) {
        setCategorisedProcessMaps([...categorisedProcessMaps]);
      }
    },
    [categorisedProcessMaps]
  );

  const onCategoryDeleted = useCallback(() => {
    fetchCategorizedProcessMaps();
  }, []);

  useEffect(() => {
    const processMapSubscriberId = 'flowingly.runner.processmap';
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event, message: string) => updateFlowModelName(message),
      processMapSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_SETUP_CATEGORY_DELETED',
      onCategoryDeleted,
      processMapSubscriberId
    );
    pubSubService.subscribe<IFlowModelDeletedMessage>(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      (eventName, message) => {
        removeFlowModel(message.flowModelId);
      },
      processMapSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      (event, message: IFlowModelPublishedMessage) => {
        const user = getService('sessionService').getUser();
        const userCanSeeFlowModel =
          message.publishType === FlowModelPublishType.ProcessMap &&
          (message.isPublishedToEveryone ||
            message.publishedToUserIds.includes(user.id) ||
            message.publishedToTeamIds.some((teamId) =>
              user.teams.some((userTeam) => userTeam.id === teamId)
            ));
        removeFlowModel(message.flowModelId);
        if (userCanSeeFlowModel) {
          addFlowModel(message.published);
        }
      },
      processMapSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      (event, message: IFlowModelUnpublishedMessage) => {
        removeFlowModel(message.flowModelId);
      },
      processMapSubscriberId
    );
    pubSubService.subscribe(
      'SIGNALR_ACTOR_DELETED',
      (event, params: string) => removeDeletedActor(params),
      processMapSubscriberId
    );

    return () => {
      pubSubService.unsubscribeAll(processMapSubscriberId);
    };
  }, [
    removeFlowModel,
    removeDeletedActor,
    updateFlowModelName,
    onCategoryDeleted
  ]);

  const fetchCategorizedProcessMaps = async () => {
    appInsightsService.startEventTimer('flowLoadProcessMapList');
    const categorisedProcessMaps =
      await flowListManager.getCategorizedProcessMaps(false);
    pubSubService.publish('PROCESSMAPV2_LOADED', {
      loaded: true,
      count: categorisedProcessMaps.length
    });
    setCategorisedProcessMaps(
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        categorisedProcessMaps
      )
    );
    setIsDataReady(true);
    appInsightsService.trackMetricIfTimerExist('flowLoadProcessMapList');
  };

  const filterFlowsByCategory = (
    id: string,
    categoryIndex?: string,
    categoryName?: string,
    expandedCategory?: IExpandedCategoryInfo
  ) => {
    if (expandedCategory) {
      flowListManager.setExpandedCategoriesList(expandedCategory);
    }

    if (returningFromProcessView.current) {
      returningFromProcessView.current = false;
      id = flowListManager.getProcessMapCategoryId();
      setExpandedCategory(flowListManager.getExpandedCategoriesList());
    } else {
      flowListManager.setProcessMapCategoryId(id);
    }

    if (
      categorisedProcessMaps.length > 0 &&
      !runnerCategoryNavigationService.findNodeById(categorisedProcessMaps, id)
    ) {
      if (id != recentlyViewedId) {
        id = allCategoriesId;
      }
    }
    setSelectedCategoryId(id);
    if (categorisedProcessMaps.length === 0) {
      return;
    }
    if (id === allCategoriesId) {
      setFilteredProcessMaps(
        extractFlowModelsFromArray(categorisedProcessMaps)
      );
    } else if (id === recentlyViewedId) {
      setFilteredProcessMaps(viewedRecently(categorisedProcessMaps));
    } else {
      const filteredCategories = runnerCategoryNavigationService.findNodeById(
        categorisedProcessMaps,
        id
      );
      setFilteredProcessMaps(filteredCategories.flowModels);
    }
  };

  const getProcesMapsFromCategorisedProcessmaps = () => {
    if (categorisedProcessMaps.length === 0) {
      return;
    }
    const processMaps =
      runnerCategoryNavigationService.getFlowModelsFromNestedCategories(
        categorisedProcessMaps
      );
    setProcessMaps(processMaps);
    filterFlowsByCategory(selectedCategoryId);
  };

  useEffect(() => {
    fetchCategorizedProcessMaps();
  }, []);

  useEffect(() => {
    if (categorisedProcessMaps.length === 0) {
      return;
    }
    getProcesMapsFromCategorisedProcessmaps();
  }, [categorisedProcessMaps]);

  const extractFlowModelsFromArray = (categories) => {
    let result = [];
    categories.forEach((category) => {
      if (category.flowModels?.length > 0) {
        result = result.concat(category.flowModels);
      }
      if (category.items?.length > 0) {
        result = result.concat(extractFlowModelsFromArray(category.items));
      }
    });
    return result;
  };

  const viewedRecently = (categorisedProcessMaps) => {
    const result = extractFlowModelsFromArray(categorisedProcessMaps);
    const filteredData = result.filter((item) => item.viewedOn !== null);
    return filteredData.sort((a, b) => {
      return new Date(b.viewedOn).getTime() - new Date(a.viewedOn).getTime();
    });
  };

  const handleFlowModelClick = (id: string) => {
    pubSubService.publish('PROCESSMAPV2_CLICKED', id);
  };

  const selectCategory = (categoryName: string) => {
    setSelectedCategoryId(
      categorisedProcessMaps.find(({ text }) => text === categoryName).text
    );
    setNullSelectedCategoryToAllCategory();
  };

  const setNullSelectedCategoryToAllCategory = () => {
    if (selectedCategoryId == allCategoriesId) {
      setSelectedCategoryId(allCategoriesId);
    }
  };

  const updateFlowModelNameInCategorisedData = (
    categorisedProcessMaps: ITreeCategory<IPublishedFlowModel>[],
    mapId: string,
    nameToBeUpdated: string
  ) => {
    for (const category of categorisedProcessMaps) {
      const map = category.flowModels.find((x) => x.id === mapId);
      if (map) {
        map.name = nameToBeUpdated;
        return map;
      }

      if (category.items) {
        const foundMap = updateFlowModelNameInCategorisedData(
          category.items,
          mapId,
          nameToBeUpdated
        );
        if (foundMap) {
          return foundMap;
        }
      }
    }
    return undefined;
  };
  const canCreateFlowModels = permissionsService.currentUserHasPermission(
    flowinglyConstants.permissions.FLOWMODEL_CREATE
  );
  return (
    <div className="runner-page" id="process-maps">
      {isDataReady && categorisedProcessMaps?.length === 0 && (
        <EmptyState
          summaryText={
            canCreateFlowModels
              ? 'You have no Process Maps'
              : 'View Process Maps'
          }
          bodyText={
            canCreateFlowModels
              ? 'We make it easy for you to create, view and share your organizational Process Maps in one place.<br /> You do not currently have any Process Maps published.'
              : 'You do not currently have any Process Maps available to view. <br /> Please contact your administrator to have relevant Process Maps shared with you.'
          }
          buttonText={canCreateFlowModels ? 'Publish a Process Map' : ''}
          onButtonClick={
            canCreateFlowModels
              ? () => $state.go(STATE_MAPPINGS.librarydrafts)
              : undefined
          }
          demoUrl={
            !brandingService.usingDefaultBrandName()
              ? undefined
              : appConfig.processMapDemoUrl
          }
        />
      )}
      {isDataReady && categorisedProcessMaps?.length > 0 && (
        <div className="runner-page__content">
          <Category
            categorisedItems={categorisedProcessMaps}
            selectedCategoryId={selectedCategoryId}
            expandedCategory={expandedCategory}
            defaultItemId={allCategoriesId}
            addAllCategory={true}
            recentlyViewed={recentlyViewedId}
            onCategoryClick={filterFlowsByCategory}
          />
          <div className="runner-page__content__main-wrapper">
            <div className="runner-page__content__main-content">
              {filteredProcessMaps && (
                <ProcessMapsList
                  processMaps={filteredProcessMaps}
                  onFlowModelClick={handleFlowModelClick}
                  selectedCategory={selectedCategoryId}
                  showMyMapsTab={
                    processMaps.filter((x) => x.isUserProcessOwner).length > 0
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessMaps;
